
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import AdminLayout from "@/layouts/AdminLayout"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { supabase } from "@/integrations/supabase/client"
import type { Database } from "@/integrations/supabase/types"

type PromptType = "monthly_tier" | "monthly" | "tier"

export default function PromptHistory() {
  const [selectedType, setSelectedType] = useState<PromptType>("monthly_tier")

  const { data: monthlyTierPrompts } = useQuery({
    queryKey: ["monthly_tier_prompts_history"],
    enabled: selectedType === "monthly_tier",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monthly_tier_prompts")
        .select("*")
        .order("year", { ascending: false })
        .order("month", { ascending: false })

      if (error) throw error
      return data
    },
  })

  const { data: monthlyPrompts } = useQuery({
    queryKey: ["monthly_prompts_history"],
    enabled: selectedType === "monthly",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monthly_prompts")
        .select("*")
        .order("year", { ascending: false })
        .order("month", { ascending: false })

      if (error) throw error
      return data
    },
  })

  const { data: tierPrompts } = useQuery({
    queryKey: ["tier_prompts_history"],
    enabled: selectedType === "tier",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tier_prompts")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      return data
    },
  })

  const renderMonthlyTierPromptsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead>Summary</TableHead>
          <TableHead>Prompt</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {monthlyTierPrompts?.map((prompt) => (
          <TableRow key={prompt.id}>
            <TableCell>{`${prompt.month}/${prompt.year}`}</TableCell>
            <TableCell className="capitalize">{prompt.subscription_tier}</TableCell>
            <TableCell className="max-w-md truncate">{prompt.user_summary}</TableCell>
            <TableCell className="max-w-md truncate">{prompt.backend_prompt}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

  const renderMonthlyPromptsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Content</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {monthlyPrompts?.map((prompt) => (
          <TableRow key={prompt.id}>
            <TableCell>{`${prompt.month}/${prompt.year}`}</TableCell>
            <TableCell className="capitalize">{prompt.prompt_type}</TableCell>
            <TableCell className="capitalize">{prompt.subscription_tier}</TableCell>
            <TableCell>{prompt.title}</TableCell>
            <TableCell className="max-w-md truncate">{prompt.content}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

  const renderTierPromptsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tier</TableHead>
          <TableHead>Summary</TableHead>
          <TableHead>Prompt</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tierPrompts?.map((prompt) => (
          <TableRow key={prompt.id}>
            <TableCell className="capitalize">{prompt.subscription_tier}</TableCell>
            <TableCell className="max-w-md truncate">{prompt.dashboard_summary}</TableCell>
            <TableCell className="max-w-md truncate">{prompt.ai_prompt}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

  return (
    <AdminLayout title="Prompt History">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Past Prompts</CardTitle>
            <Select value={selectedType} onValueChange={(value) => setSelectedType(value as PromptType)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select prompt type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly_tier">Monthly Tier Prompts</SelectItem>
                <SelectItem value="monthly">Monthly Prompts</SelectItem>
                <SelectItem value="tier">Tier Prompts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {selectedType === "monthly_tier" && renderMonthlyTierPromptsTable()}
          {selectedType === "monthly" && renderMonthlyPromptsTable()}
          {selectedType === "tier" && renderTierPromptsTable()}
        </CardContent>
      </Card>
    </AdminLayout>
  )
}
